/**
 * PasswordChangePage — 비밀번호 변경 (로그인 후 사용자 본인).
 *
 * 【역할】
 * - 로그인한 사용자가 현재 비밀번호 확인 후 신규 비밀번호로 변경
 * - 정책 위반(길이/재사용) 시 서버 MSG_* 코드를 i18n 으로 디코드 표시
 *
 * 【주요 기능】
 * - 현재 비밀번호 / 새 비밀번호 / 새 비밀번호 확인 입력
 * - 클라이언트 검증: 8자 이상, 새 비밀번호 == 확인 일치
 * - 서버 정책 위반 코드 매핑 (MSG_INVALID_CREDENTIALS / MSG_PASSWORD_SAME_AS_OLD)
 * - 변경 성공 시 /dashboard 로 이동
 *
 * 【사용 훅】
 * - useLabel (t)
 * - useState / useNavigate / Form.useForm
 *
 * 【Backend RPC serviceName】
 * - (REST 직호출) POST /api/auth/change-password
 *
 * 【DataSet 구조】
 * - 없음 (단건 POST)
 *
 * 【포팅 원본】 react-fastapi 신규 화면 (vue-spring-fw 원본 없음)
 */
import { useState } from 'react'
import { Alert, Button, Card, Form, Input, Typography, message } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import http from '@/framework/api/axios'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title } = Typography

/**
 * PasswordChangePage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function PasswordChangePage() {
  const { t } = useLabel()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const onFinish = async (values: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      void message.error(t('MSG_PASSWORD_MISMATCH', '새 비밀번호가 일치하지 않습니다.'))
      return
    }
    setSubmitting(true)
    try {
      await http.post('/auth/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      })
      void message.success(t('MSG_PASSWORD_CHANGED', '비밀번호가 변경되었습니다.'))
      form.resetFields()
      navigate('/dashboard')
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? ''
      const codeMap: Record<string, string> = {
        MSG_INVALID_CREDENTIALS: '현재 비밀번호가 일치하지 않습니다.',
        MSG_PASSWORD_SAME_AS_OLD: '새 비밀번호는 이전 비밀번호와 달라야 합니다.'
      }
      void message.error(t(detail, codeMap[detail] ?? '비밀번호 변경 실패'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PageContainer style={{ maxWidth: 520 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        {t('LBL_PASSWORD_CHANGE', '비밀번호 변경')}
      </Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t(
          'LBL_PASSWORD_POLICY_HINT',
          '새 비밀번호는 8자 이상이며, 이전 비밀번호와 달라야 합니다. 변경 후 90일간 유효합니다.'
        )}
      />

      <Card size="small">
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="currentPassword"
            label={t('LBL_CURRENT_PASSWORD', '현재 비밀번호')}
            rules={[{ required: true }]}
          >
            <Input.Password prefix={<LockOutlined />} autoFocus />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label={t('LBL_NEW_PASSWORD', '새 비밀번호')}
            rules={[
              { required: true },
              { min: 8, message: t('MSG_PASSWORD_MIN_LENGTH', '8자 이상 입력하세요.') }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label={t('LBL_CONFIRM_PASSWORD', '새 비밀번호 확인')}
            rules={[{ required: true }]}
            dependencies={['newPassword']}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              {t('BTN_CHANGE_PASSWORD', '변경')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </PageContainer>
  )
}
